// Subscription state for the clinician topbar pill + write-CTA gating (Story 6.9),
// and — from Story 10.8 — the Profile "Subscription" section's billing variant + pill.
//
// Story 10.8 performed the "one-function source swap" the stub always anticipated:
// the body is now a real `GET /api/v1/billing/subscription-state` TanStack Query
// (mirrors `useAdlItems` — `useQuery` + `apiFetch<T>` returns the unwrapped `data`).
// The `status` discriminant is preserved, so `isSubscriptionLapsed`, the topbar pill,
// and the per-screen write-CTA gating keep their exact contract with NO logic change.
// The resolved fields (`source`/`validUntil`/`isActive`/`notes`) are ADDITIVE and
// OPTIONAL on the resolved states — every existing consumer that only reads `status`
// (TopBar, EvaluationsListScreen, WorkspaceHomeScreen, NewEvaluationButton,
// useStartEvaluation) compiles + renders identically. Do NOT wire any UI to the
// mockup's `setSubscriptionState` sim-toggle; that toggle is a demo artifact.

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";

/**
 * The `notes` marker the backend stamps on a legacy clinician who has paid but
 * not yet calibrated (`entitlements.py` `NOTES_PRE_CALIBRATION`). Renders as a
 * `pending` pill on every surface, and NEVER as "lapsed" (FR47) — that
 * discrimination is the cross-story wire contract, and this string must stay in
 * lockstep with the backend constant.
 *
 * The pill's WORDS are no longer per-surface. Every surface renders
 * "Activating after calibration" for this state, from `SUBSCRIPTION_PILL` in
 * `lib/subscription-copy.ts` — the single mapper that replaced the topbar chip's
 * and the Profile card's separate copy tables (DW-437). Story 16.6's UX §13.11
 * name for this state stands; it is the ACTIVE label the PM ruled on.
 */
export const NOTES_PRE_CALIBRATION = "Pre-calibration";

/** The resolving entitlement provider — `org_seat` selects the B2B (no-CTA) variant. */
export type SubscriptionSource =
  | "individual_subscription"
  | "org_seat"
  | "manual_override";

/** The resolved entitlement facts the Profile section reads (present on active/lapsed). */
export interface ResolvedSubscription {
  isActive: boolean;
  source: SubscriptionSource | string;
  /** Current term end (renewal date + the 30-day "Renew now" window). Null pre-clock. */
  validUntil: Date | null;
  notes: string | null;
  /**
   * Story 16.7 — when the row backing this claim was written; for the legacy
   * in-product checkout that is the payment instant. `validUntil` cannot serve
   * this purpose: it is null on exactly the pre-calibration state that needs
   * dating, and on a lapsed row it is a past instant meaning the opposite
   * thing.
   *
   * Null when the resolving claim has no such row (`org_seat` /
   * `manual_override`). Treat null as UNKNOWN, never as "just now" — a
   * time-threshold read that defaults null to the present fires immediately for
   * every user it cannot date.
   */
  claimCreatedAt: Date | null;
}

// The resolved fields are OPTIONAL on `active`/`lapsed` so pre-10.8 consumer test
// fixtures (`{ status: "active" }`) keep compiling; the real hook always populates
// them, and the Profile section only reads a resolved state after `loading` clears.
export type SubscriptionState =
  | { status: "loading" }
  | ({ status: "active" } & Partial<ResolvedSubscription>)
  | ({ status: "lapsed" } & Partial<ResolvedSubscription>);

/**
 * A resolved (`active`/`lapsed`) state with its entitlement facts GUARANTEED present.
 * Consumers that read `source`/`isActive`/`validUntil`/`notes` (the Profile section)
 * narrow to this via `isResolvedSubscription` instead of casting off the loose union —
 * so the resolved fields are fully typed (not `| undefined`) at the read site.
 */
export type ResolvedSubscriptionState =
  | ({ status: "active" } & ResolvedSubscription)
  | ({ status: "lapsed" } & ResolvedSubscription);

/**
 * Type guard: has this state resolved AND carried its entitlement facts? Runtime-checks
 * the discriminant plus a real boolean `isActive` (the real hook always sets it; a bare
 * `{ status: "active" }` test fixture does not), so it doubles as the loading/malformed
 * gate. Replaces an unchecked cast at the read site.
 */
export function isResolvedSubscription(
  state: SubscriptionState,
): state is ResolvedSubscriptionState {
  return state.status !== "loading" && typeof state.isActive === "boolean";
}

/** The raw `GET /billing/subscription-state` payload (envelope peeled by `apiFetch`). */
export interface SubscriptionStateResponse {
  is_active: boolean;
  source: string;
  valid_until: string | null;
  notes: string | null;
  /** Story 16.7 — see `ResolvedSubscription.claimCreatedAt`. */
  claim_created_at: string | null;
}

/** Root key for the subscription-state query. */
export const SUBSCRIPTION_STATE_QUERY_KEY = "subscription-state";

/**
 * The one shared base for every observer of this query (gh-622 review defer):
 * `useSubscriptionState` and `useSubscriptionStateError` must stay on the SAME
 * key/fetcher/staleTime — they share one cache entry by contract. Hoisted so
 * the two call sites cannot drift.
 */
const subscriptionStateQueryOptions = {
  queryKey: [SUBSCRIPTION_STATE_QUERY_KEY],
  queryFn: () =>
    apiFetch<SubscriptionStateResponse>("/billing/subscription-state"),
  staleTime: 60_000,
};

/**
 * Maps the raw payload (or the lack of one) to the three-way union. Exported so
 * per-observer option callbacks (Story 10.5 — the lapse re-check predicate reads
 * `query.state.data`) share the exact malformed-payload guard instead of
 * re-deriving it: anything without a boolean `is_active` is `loading`, never
 * `lapsed`.
 */
export function mapSubscriptionResponse(
  data: SubscriptionStateResponse | undefined,
): SubscriptionState {
  if (typeof data?.is_active !== "boolean") {
    return { status: "loading" };
  }
  const resolved: ResolvedSubscription = {
    isActive: data.is_active,
    source: data.source,
    validUntil: data.valid_until ? new Date(data.valid_until) : null,
    notes: data.notes,
    claimCreatedAt: data.claim_created_at
      ? new Date(data.claim_created_at)
      : null,
  };
  return data.is_active
    ? { status: "active", ...resolved }
    : { status: "lapsed", ...resolved };
}

/**
 * Per-observer refetch options (Story 10.5). The lapse surfaces poll every 30s +
 * on focus while a countdown/expired window is active; every other consumer keeps
 * the default display-read cadence. Same query key — observers only differ in
 * refetch behavior, never in data shape.
 */
export interface UseSubscriptionStateOptions {
  refetchInterval?:
    | number
    | false
    | ((query: {
        state: { data: SubscriptionStateResponse | undefined };
      }) => number | false | undefined);
  refetchOnWindowFocus?:
    | boolean
    | "always"
    | ((query: {
        state: { data: SubscriptionStateResponse | undefined };
      }) => boolean | "always");
  /**
   * Keep returning the last RESOLVED state when a background refetch fails,
   * instead of collapsing to `loading`. Opt-in for the lapse surfaces
   * (Story 10.5 code review: the day-zero modal is non-dismissible by contract
   * and must not blink out on a network blip — a last-known-good CONFIRMED
   * lapse is not an "unknown" state) and the Profile section (gh-622 PR
   * review: a refetch blip must not swap the resolved card for the error
   * copy). Every other consumer keeps the default error→loading posture (a
   * transient failure never disables writes).
   */
  keepResolvedOnError?: boolean;
}

/**
 * Current subscription state (Story 10.8 — real query). Maps the async lifecycle to
 * the stable three-way union:
 *   - `isPending` OR error/unknown → `{ status: "loading" }` (never "lapsed" — read
 *     access must never be blocked on an unknown/failed state; offline & first-paint
 *     safe. A SETTLED error additionally surfaces via `useSubscriptionStateError` so
 *     the Profile section renders a terminal card instead of a spinner — gh-626).
 *   - resolved `is_active === false` → `{ status: "lapsed", ... }`.
 *   - otherwise                     → `{ status: "active", ... }`.
 *
 * `staleTime: 60s` — entitlement changes are event-driven server-side (300s cache +
 * webhook invalidation); this is a display read, not a gate, so a minute is ample.
 */
export function useSubscriptionState(
  options: UseSubscriptionStateOptions = {},
): SubscriptionState {
  const { keepResolvedOnError, ...queryOptions } = options;
  const query = useQuery({
    ...subscriptionStateQueryOptions,
    ...queryOptions,
  });

  // Unknown → loading. Covers pending, errored, AND a resolved-but-malformed payload
  // (no boolean `is_active`). `isSubscriptionLapsed` stays false for all of these, so a
  // transient failure / offline / garbage response never disables writes — AC7:
  // only a CONFIRMED known-inactive state is "lapsed" (never "unknown").
  // An errored query that still HOLDS a previously-resolved payload may opt in to keep
  // it (`keepResolvedOnError` — the lapse surfaces); an errored FIRST fetch has no
  // payload to keep and stays `loading` either way.
  if (query.isPending || (query.isError && !(keepResolvedOnError && query.data))) {
    return { status: "loading" };
  }

  return mapSubscriptionResponse(query.data);
}

/**
 * True when the subscription-state query has SETTLED in error (retries
 * exhausted) with NO cached payload to fall back on — read by the Profile
 * section (gh-626) to render a terminal "couldn't load" card instead of an
 * endless "Loading subscription…".
 *
 * First-fetch-only by design (gh-622 PR review): a failed background REFETCH
 * also sets `isError`, but the cache still holds the previously-resolved
 * payload — the Profile section keeps rendering it (`keepResolvedOnError`),
 * so flipping true there would swap a good resolved card for the error copy.
 * Only an error with `data === undefined` (nothing was ever loaded) is a
 * terminal unknown.
 *
 * Deliberately a SEPARATE read: `useSubscriptionState`'s union keeps mapping
 * error→`loading` so `isSubscriptionLapsed` stays `false` on any unknown state
 * and writes are never disabled by a transient failure (AC7). A second
 * observer on the same query key shares the cache entry — no extra fetch.
 */
export function useSubscriptionStateError(): boolean {
  const query = useQuery({ ...subscriptionStateQueryOptions });
  return query.isError && query.data === undefined;
}

/**
 * True only when the subscription is known-inactive. While `loading` (which now also
 * absorbs the unknown/errored/offline case) and while `active` this is `false`, so
 * read access is never blocked on an unknown state (AC 13; UX §13.11/§13.12).
 *
 * ⚠️ gh-556 — this is the WRITE-GATING predicate ("known inactive"), NOT a label:
 * the backend refuses writes (403 BILL-001) for every inactive state, including
 * pre-calibration and a pre-start org contract. Surfaces that SAY "lapsed" must
 * key on `subscriptionPill` (`lib/subscription-copy.ts`) instead — an inactive
 * state without a passed lapse instant has never lapsed (13.6 AC5), and that
 * mapper is now the only thing allowed to answer the question.
 */
export function isSubscriptionLapsed(state: SubscriptionState): boolean {
  return state.status === "lapsed";
}

/**
 * The RAW `notes` discriminator (gh-556), for the pure helpers that are handed
 * entitlement facts rather than the query state — `OrgSeatVariant` and the
 * LearnWorlds renewal predicate in `SubscriptionSection.tsx`. Story 10.15
 * (gh-643 F4) made this the single definition of the comparison: every call site
 * reads either this or `isPreCalibrationSubscription` below, and no site
 * re-derives `notes === NOTES_PRE_CALIBRATION` on its own.
 *
 * Prefer `isPreCalibrationSubscription` wherever a `SubscriptionState` is in
 * hand — it also refuses the unresolved states, which this one cannot see.
 */
export function isPreCalibrationNotes(notes: string | null | undefined): boolean {
  return notes === NOTES_PRE_CALIBRATION;
}

/**
 * True when the inactive state is the paid-but-uncalibrated marker (FR47) — the
 * account needs calibration, not billing. Keyed on the backend's `notes`
 * discriminator, never on inactivity alone (gh-556).
 */
export function isPreCalibrationSubscription(state: SubscriptionState): boolean {
  return state.status === "lapsed" && isPreCalibrationNotes(state.notes);
}

/**
 * True ONLY for a CONFIRMED lapse — the states the lapsed surfaces (workspace
 * banner + read-only intro, "Subscription lapsed" copy) may fire on (gh-556):
 *
 *   - pre-calibration is never a lapse (it's a calibration state, FR47);
 *   - an org seat lapses only at a PASSED lapse instant — `validUntil === null`
 *     means "never started / no instant", i.e. inactive, NOT lapsed (13.6 AC5).
 *     The Profile card's private copy of this guard is gone: its org branch now
 *     calls THIS function through `selectSubscriptionPillState`
 *     (`lib/subscription-copy.ts`). `deriveLapseStatus` in useLapseStatus.ts is
 *     the one remaining sibling to keep in lockstep;
 *   - every other inactive state (B2C lapse, never-subscribed fallback, bare
 *     pre-10.8 fixtures) keeps today's lapsed posture (the 10.5 surface).
 */
export function isSubscriptionLapseConfirmed(
  state: SubscriptionState,
  now: Date = new Date(),
): boolean {
  if (state.status !== "lapsed") return false;
  if (isPreCalibrationSubscription(state)) return false;
  if (state.source === "org_seat") {
    return state.validUntil != null && state.validUntil.getTime() <= now.getTime();
  }
  return true;
}
